/**
 * Service de gestion de la maintenance
 * Détecte le mode maintenance et gère les restrictions d'accès
 */

export interface MaintenanceStatus {
  isMaintenanceMode: boolean;
  maintenanceType: 'full' | 'partial' | 'none';
  message?: string;
}

class MaintenanceService {
  private static instance: MaintenanceService;
  private maintenanceStatus: MaintenanceStatus = {
    isMaintenanceMode: false,
    maintenanceType: 'none'
  };

  private constructor() {
    this.checkMaintenanceStatus();
  }

  public static getInstance(): MaintenanceService {
    if (!MaintenanceService.instance) {
      MaintenanceService.instance = new MaintenanceService();
    }
    return MaintenanceService.instance;
  }

  /**
   * Vérifie le statut de maintenance via les headers HTTP
   */
  private async checkMaintenanceStatus(): Promise<void> {
    try {
      // En production, vérifier les headers HTTP via une requête simple
      if (process.env.NODE_ENV === 'production') {
        try {
          // Utiliser une requête GET vers la racine pour éviter les erreurs CORS
          const response = await fetch('/', {
            method: 'GET',
            cache: 'no-cache'
          });
          
          if (response && response.ok) {
            const maintenanceHeader = response.headers.get('X-Maintenance-Mode');
            
            if (maintenanceHeader === 'partial') {
              this.maintenanceStatus = {
                isMaintenanceMode: true,
                maintenanceType: 'partial',
                message: 'Le site est en maintenance partielle. La connexion est temporairement indisponible.'
              };
              return;
            } else if (maintenanceHeader === 'full') {
              this.maintenanceStatus = {
                isMaintenanceMode: true,
                maintenanceType: 'full',
                message: 'Le site est en maintenance complète.'
              };
              return;
            }
          }
        } catch (fetchError) {
          // En cas d'erreur, forcer le mode maintenance partiel car la config Firebase l'indique
          this.maintenanceStatus = {
            isMaintenanceMode: true,
            maintenanceType: 'partial',
            message: 'Le site est en maintenance partielle. La connexion est temporairement indisponible.'
          };
          return;
        }
      }
      
      // Fallback: En développement, vérifier le fichier firebase.json
      if (process.env.NODE_ENV === 'development') {
        try {
          const response = await fetch('/firebase.json', {
            cache: 'no-cache'
          });
          
          if (response && response.ok) {
            const config = await response.json();
            
            // Chercher dans les headers pour X-Maintenance-Mode
            const headers = config.hosting?.headers || [];
            for (const headerGroup of headers) {
              if (headerGroup.headers) {
                for (const header of headerGroup.headers) {
                  if (header.key === 'X-Maintenance-Mode') {
                    if (header.value === 'partial') {
                      this.maintenanceStatus = {
                        isMaintenanceMode: true,
                        maintenanceType: 'partial',
                        message: 'Le site est en maintenance partielle. La connexion est temporairement indisponible.'
                      };
                      return;
                    } else if (header.value === 'full') {
                      this.maintenanceStatus = {
                        isMaintenanceMode: true,
                        maintenanceType: 'full',
                        message: 'Le site est en maintenance complète.'
                      };
                      return;
                    }
                  }
                }
              }
            }
          }
        } catch (fetchError) {
          // Erreur lors de la lecture de firebase.json - continuer
        }
      }
      
      // Aucune maintenance détectée
      this.maintenanceStatus = {
        isMaintenanceMode: false,
        maintenanceType: 'none'
      };

    } catch (error) {
      // En cas d'erreur, on considère qu'il n'y a pas de maintenance
      this.maintenanceStatus = {
        isMaintenanceMode: false,
        maintenanceType: 'none'
      };
    }
  }

  /**
   * Retourne le statut actuel de maintenance
   */
  public getMaintenanceStatus(): MaintenanceStatus {
    return { ...this.maintenanceStatus };
  }

  /**
   * Vérifie si la connexion est autorisée
   */
  public isLoginAllowed(): boolean {
    return this.maintenanceStatus.maintenanceType !== 'partial' && 
           this.maintenanceStatus.maintenanceType !== 'full';
  }

  /**
   * Vérifie si l'inscription est autorisée
   */
  public isRegistrationAllowed(): boolean {
    return this.maintenanceStatus.maintenanceType !== 'partial' && 
           this.maintenanceStatus.maintenanceType !== 'full';
  }

  /**
   * Vérifie si la landing page est accessible
   */
  public isLandingPageAllowed(): boolean {
    return this.maintenanceStatus.maintenanceType !== 'full';
  }

  /**
   * Retourne le message de maintenance
   */
  public getMaintenanceMessage(): string {
    return this.maintenanceStatus.message || 'Le site est temporairement en maintenance.';
  }

  /**
   * Force la vérification du statut de maintenance
   */
  public async refreshMaintenanceStatus(): Promise<MaintenanceStatus> {
    await this.checkMaintenanceStatus();
    return this.getMaintenanceStatus();
  }
}

export default MaintenanceService.getInstance();